/**
 * Diff computation utilities for real-time document streaming.
 *
 * Uses LINE-BASED diffing with similarity thresholds.
 * When lines are significantly different (< 70% similar), treat as full replacement.
 * For high-similarity lines (>= 70%), use inline character-level diffing to show
 * only the actual changed words/characters.
 */

// Similarity threshold - below this, treat as full replacement
const SIMILARITY_THRESHOLD = 0.7;

/**
 * Compute inline word-level diff between two similar lines.
 * Groups contiguous changes together for readability.
 *
 * Example:
 *   old: "The quick brown fox jumps over"
 *   new: "The quick red fox leaps over"
 *   result: "The quick ~~brown~~ ==red== fox ~~jumps~~ ==leaps== over"
 *
 * NOT: "==The== ~~The~~ quick ~~brown~~ ==red== fox ~~jumps~~ ==leaps== ~~over~~ ==over=="
 */
function computeInlineDiff(oldLine: string, newLine: string): string {
  const oldTrimmed = oldLine.trimEnd();
  const newTrimmed = newLine.trimEnd();

  // Extract markdown prefix from the NEW line (preserve structure)
  const { prefix, content: newContent } = extractMarkdownPrefix(newTrimmed);
  const { content: oldContent } = extractMarkdownPrefix(oldTrimmed);

  // If content is identical after trimming, no diff needed
  if (oldContent === newContent) {
    return newTrimmed;
  }

  // Split into words (not keeping whitespace as separate tokens)
  const oldWords = oldContent.split(/\s+/).filter(w => w.length > 0);
  const newWords = newContent.split(/\s+/).filter(w => w.length > 0);

  // Find common prefix (words that match from the start)
  let prefixEnd = 0;
  while (prefixEnd < oldWords.length && prefixEnd < newWords.length && oldWords[prefixEnd] === newWords[prefixEnd]) {
    prefixEnd++;
  }

  // Find common suffix (words that match from the end)
  let oldSuffixStart = oldWords.length;
  let newSuffixStart = newWords.length;
  while (
    oldSuffixStart > prefixEnd &&
    newSuffixStart > prefixEnd &&
    oldWords[oldSuffixStart - 1] === newWords[newSuffixStart - 1]
  ) {
    oldSuffixStart--;
    newSuffixStart--;
  }

  // Build result: common prefix + deletions + additions + common suffix
  const result: string[] = [];

  // Add common prefix
  for (let i = 0; i < prefixEnd; i++) {
    result.push(oldWords[i]);
  }

  // Add deleted words (from old, between prefix and suffix)
  const deletedWords = oldWords.slice(prefixEnd, oldSuffixStart);
  if (deletedWords.length > 0) {
    result.push(`~~${deletedWords.join(' ')}~~`);
  }

  // Add added words (from new, between prefix and suffix)
  const addedWords = newWords.slice(prefixEnd, newSuffixStart);
  if (addedWords.length > 0) {
    result.push(`==${addedWords.join(' ')}==`);
  }

  // Add common suffix
  for (let i = oldSuffixStart; i < oldWords.length; i++) {
    result.push(oldWords[i]);
  }

  return prefix + result.join(' ');
}

/**
 * Extract markdown prefix (heading, list markers, etc.) from a line.
 * Returns the prefix and the content separately.
 *
 * Examples:
 *   "## Header" -> { prefix: "## ", content: "Header" }
 *   "- List item" -> { prefix: "- ", content: "List item" }
 *   "1. Numbered" -> { prefix: "1. ", content: "Numbered" }
 *   "Regular text" -> { prefix: "", content: "Regular text" }
 */
function extractMarkdownPrefix(line: string): { prefix: string; content: string } {
  // Match heading markers: # ## ### #### ##### ######
  const headingMatch = line.match(/^(#{1,6}\s+)/);
  if (headingMatch) {
    return { prefix: headingMatch[1], content: line.slice(headingMatch[1].length) };
  }

  // Match unordered list markers: - * +
  const unorderedListMatch = line.match(/^([\-\*\+]\s+)/);
  if (unorderedListMatch) {
    return { prefix: unorderedListMatch[1], content: line.slice(unorderedListMatch[1].length) };
  }

  // Match ordered list markers: 1. 2. etc
  const orderedListMatch = line.match(/^(\d+\.\s+)/);
  if (orderedListMatch) {
    return { prefix: orderedListMatch[1], content: line.slice(orderedListMatch[1].length) };
  }

  // Match letter list markers: a. b. A. B. etc (often used for sub-bullets)
  const letterListMatch = line.match(/^(\s*)([a-zA-Z]+\.\s+)/);
  if (letterListMatch) {
    const fullPrefix = letterListMatch[1] + letterListMatch[2];
    return { prefix: fullPrefix, content: line.slice(fullPrefix.length) };
  }

  // Match roman numeral list markers: i. ii. iii. iv. I. II. etc
  const romanListMatch = line.match(/^(\s*)([ivxIVX]+\.\s+)/);
  if (romanListMatch) {
    const fullPrefix = romanListMatch[1] + romanListMatch[2];
    return { prefix: fullPrefix, content: line.slice(fullPrefix.length) };
  }

  // Match blockquote markers: >
  const blockquoteMatch = line.match(/^(>\s*)/);
  if (blockquoteMatch) {
    return { prefix: blockquoteMatch[1], content: line.slice(blockquoteMatch[1].length) };
  }

  // No markdown prefix
  return { prefix: '', content: line };
}

/**
 * Wrap content with annotation markers, keeping markdown prefix outside.
 *
 * Examples:
 *   wrapWithAnnotation("## Header", "==") -> "## ==Header=="
 *   wrapWithAnnotation("Regular text", "~~") -> "~~Regular text~~"
 */
function wrapWithAnnotation(line: string, marker: '==' | '~~'): string {
  const trimmed = line.trimEnd();
  const { prefix, content } = extractMarkdownPrefix(trimmed);

  if (!content.trim()) {
    // Empty content after prefix, don't annotate
    return trimmed;
  }

  return `${prefix}${marker}${content}${marker}`;
}

/**
 * Calculate similarity ratio between two strings using Levenshtein distance.
 */
function calculateSimilarity(a: string, b: string): number {
  if (a === b) return 1.0;
  if (!a.length || !b.length) return 0.0;

  // Simple overlap-based similarity (faster than full Levenshtein)
  // Count matching words
  const aWords = new Set(a.toLowerCase().split(/\s+/));
  const bWords = new Set(b.toLowerCase().split(/\s+/));

  let matches = 0;
  for (const word of aWords) {
    if (bWords.has(word)) matches++;
  }

  const totalUnique = new Set([...aWords, ...bWords]).size;
  if (totalUnique === 0) return 1.0;

  return matches / totalUnique;
}

/**
 * Split document into lines, preserving line breaks.
 */
function splitIntoLines(content: string): string[] {
  if (!content) return [];
  // Split on newlines but keep the newline with each line
  return content.split(/(?<=\n)/);
}

/**
 * Find the best matching line in oldLines for a given new line.
 * Returns index and similarity score, or -1 if no good match.
 */
/**
 * Normalize a string for comparison - handles unicode and whitespace variations.
 * Preserves newlines but normalizes horizontal whitespace.
 */
function normalizeForComparison(str: string): string {
  return str
    .trim()
    .replace(/[^\S\n]+/g, ' ')  // Normalize horizontal whitespace only (preserve newlines)
    .replace(/[\u200B-\u200D\uFEFF]/g, '')  // Remove zero-width chars
    .normalize('NFC');  // Normalize unicode
}

function findBestMatch(
  newLine: string,
  oldLines: string[],
  usedIndices: Set<number>
): { index: number; similarity: number } {
  let bestIndex = -1;
  let bestSimilarity = 0;

  const newNormalized = normalizeForComparison(newLine);

  for (let i = 0; i < oldLines.length; i++) {
    if (usedIndices.has(i)) continue;

    const oldNormalized = normalizeForComparison(oldLines[i]);

    // Check for exact match first (fast path) - using normalized comparison
    if (newNormalized === oldNormalized) {
      return { index: i, similarity: 1.0 };
    }

    const similarity = calculateSimilarity(newNormalized, oldNormalized);
    if (similarity > bestSimilarity) {
      bestSimilarity = similarity;
      bestIndex = i;
    }
  }

  return { index: bestIndex, similarity: bestSimilarity };
}

/**
 * Compute annotated diff between old and new content using LINE-BASED diffing.
 *
 * @param oldContent - Previous document content (clean, no annotations)
 * @param newContent - New document content (clean, no annotations)
 * @returns Annotated string with ==additions== and ~~deletions~~
 */
export function computeAnnotatedDiff(
  oldContent: string,
  newContent: string
): string {
  // Handle edge cases
  if (!oldContent && !newContent) {
    return '';
  }
  if (!oldContent) {
    // First turn - everything is new, don't annotate
    return newContent;
  }
  if (!newContent) {
    // Document deleted entirely
    return `~~${oldContent}~~`;
  }
  // Fast path: exact match (no diff needed)
  if (oldContent === newContent) {
    return newContent;
  }
  // Fast path: normalized match (whitespace/unicode/line-ending differences only)
  // Normalize: trim, normalize line endings, collapse horizontal whitespace,
  // remove zero-width chars, normalize unicode. PRESERVE blank lines for structure.
  const normalizeContent = (s: string) => s
    .trim()
    .replace(/\r\n/g, '\n')           // Normalize line endings
    .replace(/[^\S\n]+/g, ' ')        // Collapse horizontal whitespace only (preserve newlines)
    .replace(/[\u200B-\u200D\uFEFF]/g, '')  // Remove zero-width chars
    .normalize('NFC');
  const oldNormalized = normalizeContent(oldContent);
  const newNormalized = normalizeContent(newContent);
  if (oldNormalized === newNormalized) {
    return newContent;
  }

  const oldLines = splitIntoLines(oldContent);
  const newLines = splitIntoLines(newContent);

  const result: string[] = [];
  const usedOldIndices = new Set<number>();

  // Track which old lines have been matched
  const oldLineMatched = new Array(oldLines.length).fill(false);

  // Process each new line
  for (const newLine of newLines) {
    if (!newLine.trim()) {
      // Empty/whitespace line - just include it
      result.push(newLine);
      continue;
    }

    // Find best matching old line
    const { index: matchIndex, similarity } = findBestMatch(
      newLine,
      oldLines,
      usedOldIndices
    );

    if (matchIndex >= 0 && similarity >= SIMILARITY_THRESHOLD) {
      // Good match found - mark any unmatched old lines before this as deleted
      for (let i = 0; i < matchIndex; i++) {
        if (!oldLineMatched[i] && !usedOldIndices.has(i) && oldLines[i].trim()) {
          result.push(wrapWithAnnotation(oldLines[i], '~~') + '\n');
          usedOldIndices.add(i);
          oldLineMatched[i] = true;
        }
      }

      usedOldIndices.add(matchIndex);
      oldLineMatched[matchIndex] = true;

      if (similarity === 1.0) {
        // Exact match - no annotation needed
        result.push(newLine);
      } else {
        // Similar line - use inline character-level diff to show only changed parts
        const inlineDiff = computeInlineDiff(oldLines[matchIndex], newLine);
        result.push(inlineDiff + '\n');
      }
    } else {
      // No good match - this is a new line
      result.push(wrapWithAnnotation(newLine, '==') + '\n');
    }
  }

  // Mark any remaining unmatched old lines as deleted
  for (let i = 0; i < oldLines.length; i++) {
    if (!oldLineMatched[i] && oldLines[i].trim()) {
      result.push(wrapWithAnnotation(oldLines[i], '~~') + '\n');
    }
  }

  return result.join('');
}

/**
 * Streaming diff that doesn't prematurely mark content as deleted.
 * Shows new content with additions, keeps remaining old content unchanged.
 *
 * Key difference from computeAnnotatedDiff: doesn't mark unmatched old lines
 * as deleted - instead appends them unchanged (they haven't been reached yet).
 */
function computeStreamingDiffPartial(
  oldContent: string,
  streamedContent: string
): string {
  // Fast path: normalized match (no diff needed, just return new content)
  // PRESERVE blank lines for markdown structure
  const normalizeContent = (s: string) => s
    .trim()
    .replace(/\r\n/g, '\n')
    .replace(/[^\S\n]+/g, ' ')  // Collapse horizontal whitespace only (preserve newlines)
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .normalize('NFC');
  if (normalizeContent(oldContent) === normalizeContent(streamedContent)) {
    return streamedContent;
  }

  const oldLines = splitIntoLines(oldContent);
  const newLines = splitIntoLines(streamedContent);

  const result: string[] = [];
  const usedOldIndices = new Set<number>();

  // Process each streamed line
  for (const newLine of newLines) {
    if (!newLine.trim()) {
      result.push(newLine);
      continue;
    }

    // Find best matching old line
    const { index: matchIndex, similarity } = findBestMatch(
      newLine,
      oldLines,
      usedOldIndices
    );

    if (matchIndex >= 0 && similarity >= SIMILARITY_THRESHOLD) {
      // Mark old lines BEFORE this match as deleted (they were skipped)
      for (let i = 0; i < matchIndex; i++) {
        if (!usedOldIndices.has(i) && oldLines[i].trim()) {
          result.push(wrapWithAnnotation(oldLines[i], '~~') + '\n');
          usedOldIndices.add(i);
        }
      }

      usedOldIndices.add(matchIndex);

      if (similarity === 1.0) {
        // Exact match - no annotation
        result.push(newLine);
      } else {
        // Similar line - use inline character-level diff to show only changed parts
        const inlineDiff = computeInlineDiff(oldLines[matchIndex], newLine);
        result.push(inlineDiff + '\n');
      }
    } else {
      // No match - this is a new addition
      result.push(wrapWithAnnotation(newLine, '==') + '\n');
    }
  }

  // KEY DIFFERENCE: Append remaining old lines UNCHANGED (not deleted)
  // Find the highest matched index to know where streaming has reached
  const maxMatchedIndex = Math.max(-1, ...usedOldIndices);

  for (let i = maxMatchedIndex + 1; i < oldLines.length; i++) {
    if (!usedOldIndices.has(i)) {
      result.push(oldLines[i]);  // No strikethrough - streaming hasn't reached here yet
    }
  }

  return result.join('');
}

/**
 * Compute annotated diff for streaming updates.
 * Uses line-based diffing for cleaner output.
 *
 * @param oldContent - Previous document content (stable, clean)
 * @param streamedContent - Accumulated streamed content so far (growing)
 * @param isComplete - Whether streaming is complete (default false)
 *   - false: Don't mark unmatched old lines as deleted (they haven't arrived yet)
 *   - true: Compute full diff with all deletions marked
 * @returns Annotated string showing differences
 */
export function computeStreamingDiff(
  oldContent: string,
  streamedContent: string,
  isComplete: boolean = false
): string {
  if (!oldContent) {
    // First turn - show content without annotations
    return streamedContent;
  }

  if (!streamedContent) {
    // Nothing streamed yet - show old content unchanged
    return oldContent;
  }

  // Fast path: exact match
  if (oldContent === streamedContent) {
    return streamedContent;
  }

  // Fast path: normalized match (whitespace/unicode differences only)
  const normalizeContent = (s: string) => s
    .trim()
    .replace(/\r\n/g, '\n')
    .replace(/[^\S\n]+/g, ' ')  // Collapse horizontal whitespace only (preserve newlines)
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .normalize('NFC');
  if (normalizeContent(oldContent) === normalizeContent(streamedContent)) {
    return streamedContent;
  }

  if (isComplete) {
    // Streaming done - compute full diff with deletions
    return computeAnnotatedDiff(oldContent, streamedContent);
  }

  // DURING STREAMING: Don't mark unmatched old lines as deleted yet
  return computeStreamingDiffPartial(oldContent, streamedContent);
}

/**
 * Strip annotation markers from content.
 *
 * Applies "silent acceptance" rules:
 * - ==text== -> text (addition accepted)
 * - ~~text~~ -> "" (deletion accepted)
 *
 * Also cleans up orphaned list prefixes that remain after deletions.
 * When a list item like "1. ~~deleted text~~" is stripped, the "1. "
 * prefix was kept outside the markers for proper rendering. After
 * stripping, we need to remove the orphaned "1. " line.
 *
 * @param content - Annotated content
 * @returns Clean content with annotations applied
 */
export function stripAnnotations(content: string): string {
  if (!content) {
    return '';
  }
  // Remove == markers, keep text (addition accepted)
  let result = content.replace(/==([\s\S]*?)==/g, '$1');
  // Remove ~~ markers AND the text (deletion accepted)
  result = result.replace(/~~[\s\S]*?~~/g, '');

  // Clean up orphaned list prefixes that remain after deletions
  // These are lines that contain ONLY a list prefix with optional whitespace:
  // - Numbered lists: "1. ", "2. ", etc.
  // - Letter lists: "a. ", "b. ", "A. ", etc.
  // - Roman numerals: "i. ", "ii. ", "iv. ", etc.
  // - Unordered lists: "- ", "* ", "+ "
  const lines = result.split('\n');
  const cleanedLines = lines.filter(line => {
    const stripped = line.trim();
    // Check if line is ONLY a list prefix (possibly with leading whitespace)
    // Matches: "1. ", "a. ", "i. ", "- ", "* ", "+ " (with optional trailing space)
    if (/^(\d+\.|[a-zA-Z]+\.|[ivxIVX]+\.|-|\*|\+)\s*$/.test(stripped)) {
      return false; // Remove this orphaned prefix line
    }
    return true;
  });

  result = cleanedLines.join('\n');

  // Clean up excessive blank lines (more than 2 consecutive)
  result = result.replace(/\n{3,}/g, '\n\n');

  return result;
}

/**
 * Check if content has any annotation markers.
 *
 * @param content - Content to check
 * @returns true if content contains == or ~~ markers
 */
export function hasAnnotations(content: string): boolean {
  if (!content) {
    return false;
  }
  return /==[\s\S]*?==/.test(content) || /~~[\s\S]*?~~/.test(content);
}

/**
 * Extract annotation details from content.
 *
 * @param content - Annotated content
 * @returns Object with arrays of additions and deletions
 */
export function extractAnnotations(content: string): {
  additions: string[];
  deletions: string[];
} {
  const additions: string[] = [];
  const deletions: string[] = [];

  if (!content) {
    return { additions, deletions };
  }

  // Extract additions (==text==)
  const additionMatches = content.matchAll(/==([\s\S]*?)==/g);
  for (const match of additionMatches) {
    if (match[1].trim()) {
      additions.push(match[1]);
    }
  }

  // Extract deletions (~~text~~)
  const deletionMatches = content.matchAll(/~~([\s\S]*?)~~/g);
  for (const match of deletionMatches) {
    if (match[1].trim()) {
      deletions.push(match[1]);
    }
  }

  return { additions, deletions };
}
